import { apiSlice } from './apiSlice'

// ===================== qualityApi =====================
/**
 * qualityApi — Quality Control endpoints.
 *
 * Sampling chooser tab (Access frmQualitySamplingSubForm2 + frmQualityQrySampling2):
 *  - cp-codes / contract-refs: dependent combo lookups (QualityQrySamplingChoose_CP / _S)
 *  - choose: read-only detail list + Sent/Count/% summary over view_factory_sampling3
 */

export interface QualityLookupItem {
  value: string
  label: string
}

export interface SamplingChooseRow {
  id: number
  psDate: string | null
  clientLotId: string | null
  lotSizeMt: number | null
  weightPerContMt: number | null
  licenseCertNo: string | null
  result: string | null
  rejectReason: string | null
}

export interface SamplingSummaryMetric {
  value: number
  count: number
  per: number
}

export interface SamplingChooseSummary {
  buyersRef: string | null
  clientSampleRef: string | null
  deliveryTo: string | null
  quantity: number | null
  quality: string | null
  certification: string | null
  package: string | null
  total: SamplingSummaryMetric
  accept: SamplingSummaryMetric
  reject: SamplingSummaryMetric
  waiting: SamplingSummaryMetric
}

export interface SamplingChooseResponse {
  rows: SamplingChooseRow[]
  summary: SamplingChooseSummary
  total: number
}

const unwrapItems = (resp: { items?: QualityLookupItem[] } | null | undefined): QualityLookupItem[] =>
  resp && Array.isArray(resp.items) ? resp.items : []

export const qualityApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getSamplingCpCodes: b.query<QualityLookupItem[], void>({
      query: () => '/quality/sampling/cp-codes',
      transformResponse: unwrapItems,
      providesTags: [{ type: 'Quality', id: 'cp-codes' }],
    }),
    getSamplingContractRefs: b.query<QualityLookupItem[], { cpCode?: string } | void>({
      query: (arg) => ({
        url: '/quality/sampling/contract-refs',
        params: arg && arg.cpCode ? { cpCode: arg.cpCode } : undefined,
      }),
      transformResponse: unwrapItems,
      providesTags: [{ type: 'Quality', id: 'contract-refs' }],
    }),
    getSamplingChoose: b.query<SamplingChooseResponse, { cpCode?: string; contractRef?: string }>({
      query: (arg) => ({
        url: '/quality/sampling/choose',
        params: {
          ...(arg.cpCode ? { cpCode: arg.cpCode } : {}),
          ...(arg.contractRef ? { contractRef: arg.contractRef } : {}),
        },
      }),
      providesTags: [{ type: 'Quality', id: 'choose' }],
    }),
  }),
})

export const {
  useGetSamplingCpCodesQuery,
  useGetSamplingContractRefsQuery,
  useGetSamplingChooseQuery,
} = qualityApi

// ===================== qualityNestleSampleApi =====================
/**
 * Quality Control › Nestle Sample (Sampling Export) datasheet.
 *
 * Mirrors FastAPI `NestleSampleOut` — Access `frmQualityQrySamplingExp` over
 * view `tlc_factory.view_factory_sampling3_exp2`. The view exposes generic
 * `col1..col25` aliases; each maps to an Access Label.Caption in the list view.
 */
export interface NestleSample {
  id: number
  col1: string | null
  col2: string | null
  col3: string | null
  col4: string | null
  col5: string | null
  col6: string | null
  col7: string | null
  col8: string | null
  col9: string | null
  col10: string | null
  col11: string | null
  col12: string | null
  col13: number | string | null
  col14: number | string | null
  col15: string | null
  col16: string | null
  col17: string | null
  col18: string | null
  col19: string | null
  col20: string | null
  col21: string | null
  col22: string | null
  col23: string | null
  col24: string | null
  col25: string | null
}

export interface NestleSampleListResponse {
  items: NestleSample[]
  total: number
  page: number
  pageSize: number
}

export interface NestleSampleListQuery {
  page: number
  pageSize: number
  startDate?: string
  endDate?: string
}

const qualityNestleSampleApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNestleSamples: builder.query<NestleSampleListResponse, NestleSampleListQuery>({
      query: ({ page, pageSize, startDate, endDate }) => ({
        url: '/quality-nestle-samples',
        params: {
          page,
          pageSize,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
      }),
      providesTags: ['QualityNestleSample'],
    }),
  }),
})

export const { useGetNestleSamplesQuery } = qualityNestleSampleApi

// ===================== qualityPssLabelApi =====================
/** One row of `tlc_factory.view_factory_sampling_psslable2` (Access record source
 *  for frmQualityPssLableSubForm). Only label-relevant fields are typed. */
export interface PssLabelRecord {
  sample_no: string | null
  client_lot_id: string | null
  contract_sale: string | null
  txtcontract: string | null
  consignee: string | null
  grade: string | null
  lot_size_mt: number | null
  delivery_to: string | null
  pss_terms: string | null
  sample_type: string | null
  quantity_pr: number | null
  unit_pr: string | null
  shipper_pr: string | null
  email_pr: string | null
}

/** Enriched Sample No picker option (Access cblotclient + contract/grade/lot). */
export interface PssSampleOption {
  sampleNo: string
  contractRef: string | null
  grade: string | null
  clientLotId: string | null
}

interface PrintPreviewArgs {
  sampleNo: string
  copies: number
}

/** Multi-sample print (tick nhiều mẫu ở Sampling Data). `copies` áp cho MỖI mẫu. */
interface PrintPreviewMultiArgs {
  sampleNos: string[]
  copies: number
}

export const qualityPssLabelApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPssSampleOptions: builder.query<PssSampleOption[], void>({
      query: () => '/quality/pss-label/sample-options',
      transformResponse: (resp: { data: PssSampleOption[] }) => resp.data ?? [],
    }),
    getPssLabelRecord: builder.query<PssLabelRecord, string>({
      query: (sampleNo) => `/quality/pss-label/record?sampleNo=${encodeURIComponent(sampleNo)}`,
      transformResponse: (resp: { data: PssLabelRecord }) => resp.data,
    }),
    getPssLabelPrintPreview: builder.query<
      { title: string; html: string; message?: string },
      PrintPreviewArgs
    >({
      query: ({ sampleNo, copies }) =>
        `/quality/pss-label/print/preview?sampleNo=${encodeURIComponent(sampleNo)}&copies=${copies}`,
      keepUnusedDataFor: 0,
    }),
    // In gộp nhiều mẫu (POST, body { sampleNos, copies }) → 1 HTML, 4 nhãn/trang A4.
    getPssLabelPrintPreviewMulti: builder.mutation<
      { title: string; html: string; message?: string },
      PrintPreviewMultiArgs
    >({
      query: ({ sampleNos, copies }) => ({
        url: '/quality/pss-label/print/preview-multi',
        method: 'POST',
        body: { sampleNos, copies },
      }),
    }),
  }),
})

export const {
  useGetPssSampleOptionsQuery,
  useGetPssLabelRecordQuery,
  useLazyGetPssLabelPrintPreviewQuery,
  useGetPssLabelPrintPreviewMultiMutation,
} = qualityPssLabelApi

// ===================== qualitySamplingApi =====================
/** Mirrors FastAPI `QualitySamplingOut` (camelCase aliases) — frmQualityQrySampling datasheet. */
export interface QualitySampling {
  id: number
  sampleNo: string | null
  psDate: string | null
  psSentDate: string | null
  tlcContractRef: string | null
  doNo: string | null
  warehouseCode: string | null
  whSamplingName: string | null
  lotId: string | null
  clientLotId: string | null
  origin: string | null
  lotSizeMt: number | null
  weightPerContMt: number | null
  licenseCertNo: string | null
  certUnit: string | null
  qtyPreshipmentClient: number | null
  qtyPreshipmentSupervisor: number | null
  cost: number | null
  qtyAftershipmentClient: number | null
  qtyAftershipmentSupervisor: number | null
  receiver: string | null
  receivingAuthority: string | null
  inspectionBy: string | null
  sealNo: string | null
  note: string | null
  resultReceiptDate: string | null
  result: string | null
  rejectReason: string | null
  clientSampleRef: string | null
  note2: string | null
  createdDate: string | null
  updatedDate: string | null
  createdBy: string | null
  updatedBy: string | null
  isStatus: number | null
  loadingType: string | null
  grade: string | null
  buyersRef: string | null
  deliveryTo: string | null
  quality: string | null
  certification: string | null
  cpCode: string | null
  txtfullLotNo: string | null
  loadingDate: string | null
  consignee: string | null
  fumigationNote: string | null
  sampleType: string | null
  consigneeOptional: string | null
  consigneeOptionalName: string | null
  txtBuyer: string | null
  sQuantity: number | null
  sPackage: string | null
  sPackageName: string | null
  alertTxt: string | null
  sampleStatus: string | null
  statusDate: string | null
  statusReason: string | null
}

export interface QualitySamplingListResponse {
  items: QualitySampling[]
  total: number
  page: number
  pageSize: number
}

export interface QualitySamplingListQuery {
  page: number
  pageSize: number
  dateFrom?: string
  dateTo?: string
}

const qualitySamplingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getQualitySamplings: builder.query<QualitySamplingListResponse, QualitySamplingListQuery>({
      query: ({ page, pageSize, dateFrom, dateTo }) => ({
        url: '/quality-samplings',
        params: {
          page,
          pageSize,
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      }),
      providesTags: ['QualitySampling'],
    }),

    deleteQualitySampling: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/quality-samplings/${id}`, method: 'DELETE' }),
      invalidatesTags: ['QualitySampling'],
    }),
    // Bulk soft-delete các dòng đã tick (Access btnDelete multi-select).
    bulkDeleteQualitySampling: builder.mutation<
      { message: string; deletedCount: number; deleted: string[]; missing: number[] },
      number[]
    >({
      query: (ids) => ({ url: '/quality-samplings/bulk-delete', method: 'POST', body: { ids } }),
      invalidatesTags: ['QualitySampling'],
    }),
  }),
})

export const {
  useGetQualitySamplingsQuery,
  useDeleteQualitySamplingMutation,
  useBulkDeleteQualitySamplingMutation,
} = qualitySamplingApi


// ===================== qualitySamplingEditDoApi =====================
/**
 * samplingEditDoApi — RTK Query slice for the Quality › Sampling Data "Edit DO"
 * bulk-edit grid (Access `frmQualityQrySampling_EditMany_Add` hosting subform
 * `frmQualityQrySampling_EditMany`).
 *
 * Access flow:
 *   - Form_Load loads every eligible sample (sample_status='In Use' AND
 *     result='Accept', id DESC) into the grid, each with an editable `do_no`
 *     (RowSource `SELECT do_no FROM delivery_order_scs WHERE is_status=0`) and a
 *     `checked_row` tick.
 *   - btnSave_Click loops the ticked rows and UPDATEs `sampling.do_no` for each.
 *
 * → On the web there is no tmp table: GET returns the eligible samples + do_no
 *   option list; PUT applies the batch UPDATE for the ticked rows only.
 *
 * Reuses the shared `QualitySampling` cache tag so the bulk update invalidates
 * the Sampling Data list (Access `…!subForm.Requery`). Tag already registered on
 * apiSlice by the sibling quality_sampling module — NOT added here.
 */

/** One eligible sample row for the bulk-edit grid (camelCase). */
export interface SamplingEditDoRow {
  /** sampling.id — PK targeted by the UPDATE. */
  id: number
  sampleNo: string | null
  tlcContractRef: string | null
  /** do_no — editable per row. */
  doNo: string | null
  lotId: string | null
  clientLotId: string | null
  grade: string | null
  consignee: string | null
  result: string | null
}

export interface SamplingEditDoListResponse {
  rows: SamplingEditDoRow[]
  doOptions: string[]
}

/** One ticked row's editable payload for the batch UPDATE. */
export interface SamplingEditDoUpdateItem {
  id: number
  doNo: string | null
}

export interface SamplingEditDoUpdateRequest {
  rows: SamplingEditDoUpdateItem[]
}

const samplingEditDoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSamplesForEditDo: builder.query<SamplingEditDoListResponse, void>({
      query: () => ({ url: '/quality-sampling-edit-do' }),
      keepUnusedDataFor: 0,
    }),
    bulkUpdateSamplingDoNo: builder.mutation<{ updated: number }, SamplingEditDoUpdateRequest>({
      query: (body) => ({ url: '/quality-sampling-edit-do', method: 'PUT', body }),
      invalidatesTags: ['QualitySampling'],
    }),
  }),
})

export const {
  useGetSamplesForEditDoQuery,
  useBulkUpdateSamplingDoNoMutation,
} = samplingEditDoApi


// ===================== qualitySamplingEditPsApi =====================
/**
 * Quality › Sampling Data — "Edit PS Result" bulk edit endpoints.
 * Converted from Access `frmQualityQrySampling_EditManyResult_Add`.
 *
 * BE: app/api/v1/endpoints/quality_sampling_edit_ps.py
 *   GET  /quality-samplings/edit-ps-result?tlcContractRef=...  -> rows for the grid
 *   PUT  /quality-samplings/edit-ps-result                     -> batch update ticked rows
 *
 * Web deviation vs Access: no global tmp_quality_sampling temp table — GET queries
 * view_factory_sampling3 directly filtered by the contract ref (race-safe for many users).
 */

/** One 'In Use' sample row (mirrors BE SamplingEditPsRow). */
export interface SamplingEditPsRow {
  id: number
  sampleNo: string | null
  tlcContractRef: string | null
  lotId: string | null
  clientLotId: string | null
  grade: string | null
  consignee: string | null
  // Editable
  resultReceiptDate: string | null
  result: string | null
  rejectReason: string | null
  clientSampleRef: string | null
}

export interface SamplingEditPsListResponse {
  rows: SamplingEditPsRow[]
}

export interface SamplingEditPsUpdateItem {
  id: number
  resultReceiptDate?: string | null
  result?: string | null
  rejectReason?: string | null
  clientSampleRef?: string | null
}

export interface SamplingEditPsUpdateRequest {
  rows: SamplingEditPsUpdateItem[]
}

export interface SamplingEditPsUpdateResponse {
  updated: number
}

const BASE = '/quality-samplings/edit-ps-result'

const samplingEditPsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSamplingsForEditPs: builder.query<SamplingEditPsListResponse, string>({
      query: (tlcContractRef) => ({ url: BASE, params: { tlcContractRef } }),
      // Reuse the shared QualitySampling tag (registered in apiSlice.ts).
      providesTags: ['QualitySampling'],
    }),

    updateSamplingsPsResult: builder.mutation<SamplingEditPsUpdateResponse, SamplingEditPsUpdateRequest>({
      query: (body) => ({ url: BASE, method: 'PUT', body }),
      invalidatesTags: ['QualitySampling'],
    }),
  }),
})

export const {
  useGetSamplingsForEditPsQuery,
  useUpdateSamplingsPsResultMutation,
} = samplingEditPsApi


// ===================== qualitySamplingFormApi =====================
/** Combo option — mirrors BE SamplingLookupItem. */
export interface SamplingLookupItem {
  value: string
  label: string
}
export interface SamplingLookupResponse {
  items: SamplingLookupItem[]
  total: number
}

/** Payload for New/Edit/Copy — camelCase aliases of QualitySamplingWrite. */
export interface SamplingWrite {
  sampleNo?: string | null
  sampleType?: string | null
  sampleStatus?: string | null
  statusDate?: string | null
  statusReason?: string | null
  consigneeOptional?: string | null
  tlcContractRef?: string | null
  psDate?: string | null
  doNo?: string | null
  warehouseCode?: string | null
  lotId?: string | null
  lotSizeMt?: number | null
  licenseCertNo?: string | null
  clientLotId?: string | null
  origin?: string | null
  weightPerContMt?: number | null
  certUnit?: string | null
  grade?: string | null
  qtyPreshipmentClient?: number | null
  qtyPreshipmentSupervisor?: number | null
  cost?: number | null
  qtyAftershipmentClient?: number | null
  qtyAftershipmentSupervisor?: number | null
  receiver?: string | null
  inspectionBy?: string | null
  note?: string | null
  psSentDate?: string | null
  receivingAuthority?: string | null
  sealNo?: string | null
  resultReceiptDate?: string | null
  rejectReason?: string | null
  clientSampleRef?: string | null
  result?: string | null
  loadingType?: string | null
  note2?: string | null
  fumigationNote?: string | null
}

interface MutationResponse {
  id: number
  sampleNo: string | null
}
interface SuggestResponse {
  sampleNo: string
}
interface CheckResponse {
  exists: boolean
}
interface FormResponse {
  record: QualitySampling
}
/** Snapshot of latest prior sampling row for a contract (Access id2=0 cascade). */
export interface SamplingContractSnapshot {
  psDate?: string | null
  warehouseCode?: string | null
  lotSizeMt?: number | null
  weightPerContMt?: number | null
  licenseCertNo?: string | null
  origin?: string | null
  psSentDate?: string | null
  receiver?: string | null
  receivingAuthority?: string | null
  inspectionBy?: string | null
  sealNo?: string | null
  loadingType?: string | null
  certUnit?: string | null
  grade?: string | null
}

const LK = '/quality-sampling-lookups'

const samplingFormApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Hydrate Edit/Copy
    getSamplingForm: builder.query<FormResponse, number>({
      query: (id) => ({ url: `/quality-samplings/${id}/form` }),
    }),
    // Suggest + duplicate check (race-safe)
    suggestSampleNo: builder.query<SuggestResponse, void>({
      query: () => ({ url: '/quality-samplings/suggest/sample-no' }),
    }),
    checkSampleNo: builder.query<CheckResponse, { sampleNo: string; excludeId?: number }>({
      query: ({ sampleNo, excludeId }) => ({
        url: '/quality-samplings/check/sample-no',
        params: { sampleNo, ...(excludeId ? { excludeId } : {}) },
      }),
    }),
    createSampling: builder.mutation<MutationResponse, SamplingWrite>({
      query: (body) => ({ url: '/quality-samplings', method: 'POST', body }),
      invalidatesTags: ['QualitySampling'],
    }),
    updateSampling: builder.mutation<MutationResponse, { id: number; body: SamplingWrite }>({
      query: ({ id, body }) => ({ url: `/quality-samplings/${id}`, method: 'PUT', body }),
      invalidatesTags: ['QualitySampling'],
    }),
    // Lookups
    lkSamplingTlcContracts: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/tlc-contracts`, params: q ? { q } : {} }),
    }),
    lkSamplingDoNos: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/do-nos`, params: q ? { q } : {} }),
    }),
    lkSamplingWarehouses: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/warehouses`, params: q ? { q } : {} }),
    }),
    lkSamplingLots: builder.query<SamplingLookupResponse, { warehouseCode?: string; q?: string }>({
      query: ({ warehouseCode, q }) => ({
        url: `${LK}/lots`,
        params: { ...(warehouseCode ? { warehouseCode } : {}), ...(q ? { q } : {}) },
      }),
    }),
    lkSamplingLicenses: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/licenses`, params: q ? { q } : {} }),
    }),
    lkSamplingGrades: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/grades`, params: q ? { q } : {} }),
    }),
    lkSamplingOrigins: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/origins`, params: q ? { q } : {} }),
    }),
    lkSamplingSampleTypes: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/sample-types`, params: q ? { q } : {} }),
    }),
    lkSamplingSampleStatuses: builder.query<SamplingLookupResponse, string | undefined>({
      query: (q) => ({ url: `${LK}/sample-statuses`, params: q ? { q } : {} }),
    }),
    lkSamplingCertUnit: builder.query<{ certUnit: string | null }, string>({
      query: (licenseCertNo) => ({ url: `${LK}/cert-unit`, params: { licenseCertNo } }),
    }),
    lkSamplingContractGrade: builder.query<{ grade: string | null }, string>({
      query: (tlcContractRef) => ({ url: `${LK}/contract-grade`, params: { tlcContractRef } }),
    }),
    // Access tlc_contract_ref_Change (id2=0): snapshot latest prior sampling row.
    lkSamplingContractSnapshot: builder.query<SamplingContractSnapshot, string>({
      query: (tlcContractRef) => ({ url: `${LK}/contract-snapshot`, params: { tlcContractRef } }),
    }),
  }),
})

export const {
  useLazyGetSamplingFormQuery,
  useLazySuggestSampleNoQuery,
  useLazyCheckSampleNoQuery,
  useCreateSamplingMutation,
  useUpdateSamplingMutation,
  useLazyLkSamplingCertUnitQuery,
  useLazyLkSamplingContractGradeQuery,
  useLazyLkSamplingContractSnapshotQuery,
  useLkSamplingTlcContractsQuery,
  useLkSamplingDoNosQuery,
  useLkSamplingWarehousesQuery,
  useLkSamplingLotsQuery,
  useLkSamplingLicensesQuery,
  useLkSamplingGradesQuery,
  useLkSamplingOriginsQuery,
  useLkSamplingSampleTypesQuery,
  useLkSamplingSampleStatusesQuery,
} = samplingFormApi
